/** @module Camunda/ProcessOnInstance */
define([
	"dojo/_base/declare",
	"dojo/store/Memory",
	"mobx",
	"underscore",
	"csi",
	"csi/heading/Specs",
	"csi/html",
	"csi/locale",
	"csi/reflection",
	"csi/state/rootStore",
	"csi/store/Nodes",
	"csi/table/TablePlaceholderWithPrimaryButton",
	"csi/task/_TaskFilmstrip",
	"csi/task/util",
	"csi/xml",
	"csi/xml/Query",
	"Camunda",
	"./ProcessOnInstanceRenderer",
	"./ProcessOnInstanceTaskRenderer"
], function(declare, Memory, {runInAction}, _, csi, Specs, html, locale, reflection, {rightPaneStore}, Nodes, TablePlaceholderWithPrimaryButton, _TaskFilmstrip, TaskUtil, xml, Query,
	Camunda, ProcessOnInstanceRenderer, ProcessOnInstanceTaskRenderer) {

	const TYPE_TASK = "CamundaTask";
	var _taskSpecs;
	var _processSpecs;

	/**
	 * Return Specs for query level
	 * @param {View} defView
	 * @param {QueryLevel} ql
	 * @param {Function} classRenderer
	 */
	function _getSpecs(defView, ql, classRenderer) {
		const h = _.extend(defView.newHeading(), {
			classRenderer: classRenderer,
			m_prop: xml.PROP_NAME
		});

		const specs = new Specs();
		const spec = ql.specBuilder.getSpecForHeading(h);
		specs.add(spec);
		return specs;
	}

	return declare(_TaskFilmstrip, {
		hideExtraColumn: true,
		hideHeader: true,
		placeholderClass: TablePlaceholderWithPrimaryButton,
		plugins: [],
		prefClass: null,

		/** @property {Memory} availableTemplateStore -  store of process templates that are available for use (taking into account MultipleInstancesAllowed) */
		availableTemplateStore: null,

		/** @property {Nodes} _allTemplateStore -  store of all process templates that can be manually started on instance (regardless of MultipleInstancesAllowed) */
		_allTemplateStore: null,

		/** @property {Object<string,boolean>} _showCompletedTasks -  map of processes which are showing completed tasks (as opposed to running tasks) */
		_showCompletedTasks: null,

		/**
		 * @override
		 * Setup properties
		 */
		postMixInProperties: function() {
			this.filmstripLabel = locale.typePlural("CamundaProcessInstance");
			this._showCompletedTasks = {};
			this.inherited(arguments);
		},

		/**
		 * @override
		 * Specs for process instance need to be defined differently than for task
		 */
		getQueryLevelSpecs: function(ql, type) {
			if (type === TYPE_TASK) {
				return _taskSpecs = _taskSpecs || _getSpecs(this.defView, ql, ProcessOnInstanceTaskRenderer);
			}

			return _processSpecs = _processSpecs || _getSpecs(this.defView, ql, ProcessOnInstanceRenderer);
		},

		/**
		 * @override
		 * Do same thing as when tasks are completed in MyTasks view
		 */
		onClaimTasks: function() {
			const id = this.filmstripSelection;
			const row = this.getRefNodeById(id);
			this.redrawNode(row);
			this._refreshFilmstripViews(true);
		},

		/**
		 * @override
		 * Refresh task row and views
		 */
		onCompleteTasks: function() {
			const self = this;
			const id = this.filmstripSelection;
			const row = this.getRefNodeById(id);
			const processRow = this.getRowParent(row);
			const process = this.getRowNode(processRow);

			this.when(this.refreshHierarchyRow(processRow), function(childRows) {
				if (Camunda.isProcessInstanceActive(process)) {
					// iterate through child rows to find first task that is running
					const bFound = _.some(childRows, function(childRow) {
						const task = self.getRowNode(childRow);

						if (TaskUtil.isTaskActive(task)) {
							self.set("filmstripSelection", self.getRefNodeId(childRow));
							return true;
						}
					});

					if (bFound) {
						return;
					}
				}

				self.updateAvailableTemplates();
				self.set("filmstripSelection", self.getRefNodeId(processRow));
			});
		},

		/**
		 * @override
		 * Even though this is hierarchical view, style processes as groups
		 */
		onCreateMainQueryLevel: function(ql) {
			ql.css = "tableViewGroup";
			this.inherited(arguments);
		},

		/**
		 * @override
		 * Hide or show row based on current setting for showing running vs completed tasks
		 */
		onRowStyling: function(row, qr) {
			if (qr) {
				const node = qr.getNode();

				if (node.$Type === TYPE_TASK) {
					const processUrl = node.__Parent__;
					const process = this.cacheView.get(processUrl);
					const showRunning = Camunda.isProcessInstanceActive(process) && !this._showCompletedTasks[processUrl];
					html.setVisible(row, TaskUtil.isTaskActive(node) === showRunning);
				}
			}
		},

		/**
		 * @override
		 * Initialize expand state for running processes
		 */
		onViewResults: function(qr, resultNodes) {
			if (qr.isRoot()) {
				this.set("expandState", csi.hashArray(resultNodes, xml.nodeGetURL, Camunda.isProcessInstanceActive));
				this.updateAvailableTemplates();
			}

			this.inherited(arguments);
		},

		/**
		 * @override
		 * Query for all process definitions that can be started
		 */
		onViewStart: function(dfds) {
			const instance = this.getViewNode();
			const subtype = instance.Subtype;

			if (TaskUtil.isWorkflowAdmin(this.getContext())) {
				const filter = new Query()
					.Attr(reflection.ATTR_STATE, xml.OP_EQUALS, reflection.REVISION_APPROVED, reflection.PATH_CURRENT_REVISION)
					.Attr("ManualStartAllowed", xml.OP_EQUALS, true)
					.Attr("PrimaryType", xml.OP_EQUALS, instance.$Type);

				if (xml.isValidURL(subtype)) {
					// handle case where subtype exists
					filter.Or()
						.RefAttr("PrimarySubtype", xml.OP_EQUALS, subtype)
						.RefAttr("PrimarySubtype", xml.OP_EQUALS, csi.EMPTY_URL);
				} else {
					// handle possible case where subtype is not required or does not exist
					filter.RefAttr("PrimarySubtype", xml.OP_EQUALS, csi.EMPTY_URL);
				}

				const store = this._allTemplateStore = new Nodes({
					cachingStore: this.cacheView,
					filterQuery: filter,
					sortQuery: new Query().SortByNode(xml.PROP_NAME),
					typeNode: "CamundaProcessDefinition"
				});

				dfds.push(store.query());
			}

			this.inherited(arguments);
		},

		/**
		 * @override
		 * Navigate to selected right pane url
		 */
		onViewEnd: function() {
			this.inherited(arguments);

			const {selectedUrl} = rightPaneStore;
			if (selectedUrl) {
				_.defer(() => {
					this.set("filmstripSelection", selectedUrl);
				});
			}
		},

		/**
		 * Toggle between displaying running and completed tasks
		 * @param {string} processUrl
		 * @param {boolean} showCompleted
		 */
		toggleCompletedTasks: function(processUrl, showCompleted) {
			this._showCompletedTasks[processUrl] = showCompleted;

			const row = this.getRefNodeByUrl(processUrl);
			const children = this.getRowChildren(row);

			_.each(children, function(childRow) {
				this.onRowStyling(childRow, this.getQueryResultByDomNode(childRow));
			}, this);
		},

		/**
		 * Update available template store based on current workflows and templates
		 */
		updateAvailableTemplates: function() {
			const templateStore = this._allTemplateStore;
			var templates = templateStore && templateStore.getAllResults();

			// if there are no templates that can be started, then do not allow start action (availableTemplateStore == null)
			if (!templates || !templates.length) {
				return;
			}

			// find all templates used on active workflows
			const cache = this.cacheView;
			const workflows = this.get("store").getAllResults();
			const templatesInUse = {};

			_.each(workflows, function(workflow) {
				if (Camunda.isProcessInstanceActive(workflow)) {
					templatesInUse[cache.get(workflow.ProcessDefinition).__Parent__] = true;
				}
			});

			// filter out inactive templates
			templates = _.where(templates, {Active: true});

			// create store of templates that are either not actively being used, or allow multiple active instances
			this.availableTemplateStore = new Memory({
				data: _.map(templates, function(template) {
					return {
						disabled: !template.MultipleInstancesAllowed && !!templatesInUse[template.$URL],
						id: template.CurrentRevision,
						label: locale.getNodeName(template, cache)
					};
				})
			});
		},

		/**
		 * Return true if there are available workflows to start
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		_accessActionStart: function(ctx, action) {
			return !!this.availableTemplateStore;
		},

		/**
		 * @override
		 * Return custom type for CamundaProcessInstance
		 */
		_getNoResultType: function() {
			return "ProcessInstance";
		},

		/**
		 * @override
		 * No description needed for CamundaProcessInstance
		 */
		_getNoResultDescription: function(hasColumnFilter, hasNewAction) {
			return html.SPACE;
		},

		/**
		 * @override
		 * Close focused view rather than just toggle filmstrip mode
		 */
		_onContentClose: function(e) {
			rightPaneStore.closeSelected();
		},

		/**
		 * @override
		 * Expand focused view to full width if toggled into filmstrip mode
		 */
		_toggleFilmStripMode: function() {
			const bToggled = this.filmstripMode;

			if (this.inFilmstripMode() == bToggled) {
				return null;
			}

			if (bToggled) {
				runInAction(() => {
					rightPaneStore.forceFullWidth = true;
				});
			}

			return this.inherited(arguments);
		}
	});
});